import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DataSummaryStore } from 'src/app/features/questionnaires/store/data-summary-store';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { NoResultComponent } from 'src/app/shared/components/no-result/no-result.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { QuestionTitlePipe } from '../question-title.pipe';
import { AnswerSwitchTypePipe } from '../../genery-column/answer-switch-type.pipe';
import { EQuestionType } from 'src/app/models/questionType.model';
import { filter, map } from 'rxjs';
import { GlobalStore } from 'src/app/store/global-store';
import { environment } from 'src/environment';

@Component({
    selector: 'que-api-exposure',
    imports: [CommonModule, TranslateDirective, TranslatePipe, NoResultComponent, QuestionTitlePipe, AnswerSwitchTypePipe],
    templateUrl: './api-exposure.component.html',
    styleUrls: ['./api-exposure.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApiExposureComponent {
  #storeData = inject(DataSummaryStore);
  #globalStore = inject(GlobalStore);
  #answerSwitchPipe = new AnswerSwitchTypePipe();

  #exposureFields = toSignal(this.#globalStore.exposureFields$, { initialValue: [] });

  selectedQuestions = new Map<number, Set<number>>();
  selectAll = false;
  collapsedRecipients = new Set<number>();

  recipientsData$ = toSignal(
    this.#storeData.recipientsAnsweredApiExposureQuestionsData$.pipe(
      filter(recipients => !!recipients && recipients.length > 0),
      map(recipients =>
        recipients?.map(recipientData => {
          const questionMap = new Map<number, typeof recipientData.questions>();
          const answers = new Map<string, any>();

          recipientData.questions.forEach(qa => {
            if (qa.question?.modularQuestions?.length && qa.question.toNum == null) {
              if (!questionMap.has(qa.question.id)) {
                questionMap.set(qa.question.id, [qa]);
              }

              if (qa.answer?.modularQuestionGuid) {
                answers.set(qa.answer.modularQuestionGuid, qa.answer);
              }
            } else {
              questionMap.set(qa.question?.id || Math.random(), [qa]);
            }
          });

          const expandedQuestions = Array.from(questionMap.values()).flatMap(qaArray => {
            const qa = qaArray[0];

            if (qa.question?.modularQuestions?.length && qa.question.toNum == null) {
              return qa.question.modularQuestions.map(mq => ({
                question: { ...mq, id: 0 } as any,
                answer: (answers.get(mq.guid) || undefined) as any,
                apiExposureFieldId: (mq.apiExposureFieldId || qa.apiExposureFieldId) as any,
                apiExposureFieldName:
                  this.getFieldNameById(mq.apiExposureFieldId || qa.apiExposureFieldId) ||
                  mq.apiExposureFieldName ||
                  qa.apiExposureFieldName ||
                  mq.title,
                enableEdit: qa.enableEdit
              }));
            }

            return [
              {
                question: qa.question,
                answer: qa.answer,
                apiExposureFieldId: qa.apiExposureFieldId as any,
                apiExposureFieldName: this.getFieldNameById(qa.apiExposureFieldId) || qa.apiExposureFieldName,
                enableEdit: qa.enableEdit
              }
            ];
          });

          return {
            ...recipientData,
            questions: expandedQuestions
          };
        })
      )
    )
  );

  loadingData$ = toSignal(this.#storeData.loadingData$);
  apiExposureData$ = this.#storeData.apiExposureData$;
  currentId = toSignal(this.#storeData.currentId$);

  constructor() {
    this.#storeData.updateDisplayState('apiExposure');
  }

  private extractAnswerValue(answer: any, question: any): any {
    if (!answer) return null;

    const customAnswer = this.getApiExposureAnswerText(answer, question);
    if (customAnswer && customAnswer !== '-') {
      return customAnswer;
    }

    const pipeResult = this.#answerSwitchPipe.transform(answer, question);
    if (pipeResult && pipeResult !== '-') {
      return pipeResult;
    }

    return null;
  }

  sendSelectedToApi(): void {
    const recipients = this.recipientsData$();
    if (!recipients) return;

    const payload: any[] = [];

    this.selectedQuestions.forEach((qSet, recipIndex) => {
      const recipData = recipients[recipIndex];
      if (!recipData) return;

      const email = recipData.recipient?.user?.email;
      if (!email) return;

      const fields: any[] = [];

      qSet.forEach(qIdx => {
        const qa = recipData.questions[qIdx];
        if (!qa) return;

        const fieldId = qa.apiExposureFieldId;
        const answerValue = this.extractAnswerValue(qa.answer, qa.question);
        fields.push({ fieldId, answer: answerValue });
      });

      if (fields.length > 0) {
        payload.push({ email, fields });
      }
    });

    if (payload.length === 0) return;

    console.log('API Exposure payload', JSON.stringify(payload, null, 2));

    const questionnaireId = this.currentId() ?? 0;
    this.#storeData.updateApiExposureSelected({ questionnaireId, recipients: payload });

    this.selectedQuestions.clear();
    this.selectAll = false;
  }

  getFieldNameById(id?: number): string | undefined {
    if (!id) return undefined;

    const field = this.#exposureFields().find(f => f.id === id);
    if (!field) return undefined;

    return environment.lang === 'he' ? field.fieldName : (field.fieldNameEn || field.fieldName);
  }

  getRecipientColorClass(index: number): string {
    const colors = ['yellow', 'green', 'pink', 'blue', 'purple'];
    return colors[index % colors.length];
  }

  getApiExposureAnswerText(answer: any, question: any): string {
    if (!answer) return '-';

    if (question?.type?.id === EQuestionType.Multi && answer.multiOptionsAnswer) {
      const guidsArray = answer.multiOptionsAnswer.split(',').filter((g: string) => g.trim());
      const arr = question?.optionsList?.answerOptionItem as any[];

      if (arr && arr.length > 0) {
        const names = guidsArray
          .map((guid: string) => arr.find(opt => opt?.guid === guid.trim())?.name)
          .filter((name: string) => name);

        if (names.length > 0) return names.join(', ');
      }

      return answer.multiOptionsAnswer || '-';
    }

    return '';
  }

  toggleQuestionSelection(recipientIndex: number, questionIndex: number): void {
    if (!this.selectedQuestions.has(recipientIndex)) {
      this.selectedQuestions.set(recipientIndex, new Set<number>());
    }

    const recipientSelections = this.selectedQuestions.get(recipientIndex)!;

    if (recipientSelections.has(questionIndex)) {
      recipientSelections.delete(questionIndex);
    } else {
      recipientSelections.add(questionIndex);
    }

    if (recipientSelections.size === 0) {
      this.selectedQuestions.delete(recipientIndex);
    }

    this.updateSelectAllState();
  }

  private getSelectableQuestions(recipientIndex: number) {
    const recipient = this.recipientsData$()?.[recipientIndex];
    if (!recipient) return [];

    return recipient.questions
      .map((q, index) => ({ q, index }))
      .filter(x => x.q.enableEdit !== false);
  }

  toggleRecipientSelection(recipientIndex: number): void {
    const selectableQuestions = this.getSelectableQuestions(recipientIndex);
    if (selectableQuestions.length === 0) return;

    const allSelected = selectableQuestions.every(x =>
      this.isQuestionSelected(recipientIndex, x.index)
    );

    if (allSelected) {
      this.selectedQuestions.delete(recipientIndex);
      this.updateSelectAllState();
      return;
    }

    const recipientSelections = new Set<number>(
      selectableQuestions.map(x => x.index)
    );

    this.selectedQuestions.set(recipientIndex, recipientSelections);
    this.updateSelectAllState();
  }

  toggleSelectAll(): void {
    const recipients = this.recipientsData$();
    if (!recipients || recipients.length === 0) {
      this.selectAll = false;
      return;
    }

    const shouldSelectAll = !this.selectAll;
    this.selectAll = shouldSelectAll;

    if (!shouldSelectAll) {
      this.selectedQuestions.clear();
      return;
    }

    this.selectedQuestions.clear();

    recipients.forEach((recipientData, i) => {
      const allQuestions = new Set<number>();

      recipientData.questions.forEach((qa, j) => {
        if (qa.enableEdit !== false) {
          allQuestions.add(j);
        }
      });

      if (allQuestions.size > 0) {
        this.selectedQuestions.set(i, allQuestions);
      }
    });
  }

  isQuestionSelected(recipientIndex: number, questionIndex: number): boolean {
    return this.selectedQuestions.get(recipientIndex)?.has(questionIndex) ?? false;
  }

  isRecipientFullySelected(recipientIndex: number): boolean {
    const selectableQuestions = this.getSelectableQuestions(recipientIndex);

    return (
      selectableQuestions.length > 0 &&
      selectableQuestions.every(x => this.isQuestionSelected(recipientIndex, x.index))
    );
  }

  isRecipientPartiallySelected(recipientIndex: number): boolean {
    const selectableQuestions = this.getSelectableQuestions(recipientIndex);

    const selectedCount = selectableQuestions.filter(x =>
      this.isQuestionSelected(recipientIndex, x.index)
    ).length;

    return selectedCount > 0 && selectedCount < selectableQuestions.length;
  }

  private updateSelectAllState(): void {
    const recipients = this.recipientsData$();
    if (!recipients || recipients.length === 0) {
      this.selectAll = false;
      return;
    }

    const recipientsWithSelectableQuestions = recipients.filter(
      (_, i) => this.getSelectableQuestions(i).length > 0
    );

    if (recipientsWithSelectableQuestions.length === 0) {
      this.selectAll = false;
      return;
    }

    this.selectAll = recipients.every((_, i) => {
      const selectableQuestions = this.getSelectableQuestions(i);
      if (selectableQuestions.length === 0) return true;

      return this.isRecipientFullySelected(i);
    });
  }

  getSelectedItems(): { recipientIndex: number; questionIndex: number }[] {
    const selected: { recipientIndex: number; questionIndex: number }[] = [];

    this.selectedQuestions.forEach((questions, recipientIndex) => {
      questions.forEach(questionIndex => {
        selected.push({ recipientIndex, questionIndex });
      });
    });

    return selected;
  }

  toggleRecipientCollapse(recipientIndex: number): void {
    if (this.collapsedRecipients.has(recipientIndex)) {
      this.collapsedRecipients.delete(recipientIndex);
    } else {
      this.collapsedRecipients.add(recipientIndex);
    }
  }

  isRecipientCollapsed(recipientIndex: number): boolean {
    return this.collapsedRecipients.has(recipientIndex);
  }

  getInitials(user: any): string {
    const first = user?.firstName?.[0]?.toUpperCase() || '';
    const last = user?.lastName?.[0]?.toUpperCase() || '';
    return `${first}${last}`;
  }
}